import * as React from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import dayjs from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import { CmdLoader } from '@commander-services/gui-components';
import { useIntl } from 'react-intl';
import { useLocation, useRoute } from 'wouter';
import Table from './Table/Table';
import { URL_TRIPS_OVERVIEW, URL_TRIP_MAP } from '../../router/constants';
import AddComment from './ActionsForms/AddComment';
import AddUsage from './ActionsForms/AddUsage';
import ChangeDriver from './ActionsForms/ChangeDriver';
import TripDetail from './ActionsForms/TripDetail';
import NewRideForm from './NewRide/Form';
import TableDatepicker from '../TableDatepicker';
import TableSettings from './Settings/TableSettings';
import SummarySettings from './Settings/SummarySettings';
import FilterChips from './FilterChips/FilterChips';
import useCheckbox from './hooks/useCheckbox';
import Ridebook from './Ridebook';

import PermissionsService, { PERMISSION_REPORT } from '../../Services/PermissionsService';

import {
  RIDE_PURPOSE_ACTION,
  PARKING_PURPOSE_ACTION,
  CHANGE_DRIVER_ACTION,
  RIDE_COMMENT_ACTION,
  PARKING_COMMENT_ACTION,
  TYPE_PRIVATE_ACTION,
  TYPE_PUBLIC_ACTION,
  SHOW_RIDE_ACTION,
  NEW_RIDE_ACTION,
  ACTIONS_KEY,
  DESCENDING_KEY,
  TIMESTAMP_KEY,
  CUSTOMER_KEY,
  MONTHLY_KEY,
  WEEKLY_KEY,
  DAILY_KEY,
  OVERVIEW_KEY,
  TOTAL_KEY,
} from './constants';
import useTable from './hooks/useTable';
import { getDayjsLocale, getDefaultLanguage } from '../../Services/LanguageService';
import { IS_DRIVER } from '../Employee/EmployeeService';
import { localForageStore } from '../../store/localForageStore';
import { IDatepickerSettings, setTableDatepickerAtom } from '../../store/recoil/TableState';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import AddButton from './NewRide/AddButton';
import OnlineHelp from '../OnlineHelp';
import DayTimeFilter from './AdvancedFilters/DayTimeFilter';
import ConfirmDialog from '../ConfirmDialog';
import AdvancedFilterChips from './AdvancedFilters/AdvancedFilterChips';
import InforRideFilter from './AdvancedFilters/InfoForRideFilter';
import {
  ICheckedTableItems,
  IEndpoitData,
  IFilterTripsOverview,
  INFO_FILTER,
  IS_DAY_TIME,
  ITableSettingsData,
  ITableSummariesFilter,
  ITableSummaryData,
  ITripsOverViewRecord,
  ITripsOverViewSummary,
  ITripsOverviewAdvancedFilterForRequest,
  ITripsOverviewColumn,
  ITripsOverviewData,
  ITripsOverviewTableData,
} from './interfaces';
import ExportDownloadToast from '../Export/ExportDownloadToast';
import { customersAtom } from '../../store/recoil/customers';
import { selectedVehiclesAtom } from '../../store/recoil/vehicles';
import {
  actualSizeOfRecordsAtom,
  advancedFiltersAtom,
  checkedTableItemsAtom,
  columnsAtom,
  columnsKeyOrderAtom,
  filterForRequestAtom,
  hasMoreAtom,
  hiddenSummaryItemIndexesAtom,
  isActiveAdvanceFilterAtom,
  isModalFormOpenedAtom,
  isSummarySettingsMenuOpenedAtom,
  loadingDataAtom,
  offsetAtom,
  rideBookDataAtom,
  rideBookFiltersAtom,
  rideItemIdsAtom,
  rideItemTimestampsAtom,
  showSummariesAtom,
  sortFilterAtom,
  summaryItemIndexesAtom,
  tableSettingsStateAtom,
  tripDataAtom,
} from './TripsOverviewState';
import {
  arrayOrder,
  changeRidePrivacy,
  convertTimeToSeconds,
  getRidebookData,
  getTableSummaries,
  getUserSettings,
  handleFilterKey,
  removeRide,
  saveTableSettingAndSummary,
} from './TripsOverviewService';
import { isDemo, isNotDemo } from '../../Services/DemoService';

function TripsOverview() {
  const { formatMessage: f } = useIntl();
  const [pathname, navigate] = useLocation();
  const [, params] = useRoute(`${URL_TRIPS_OVERVIEW}/edit/:vehicleId/:rideId`);
  dayjs.extend(weekOfYear);
  const { setRideInfoRecordsState, updateRideItemsState } = useTable();

  const datepickerSettings = useRecoilValue<IDatepickerSettings | null>(setTableDatepickerAtom);
  const [tableData, setTableData] = useRecoilState<ITripsOverviewTableData | null>(
    rideBookDataAtom
  );
  const [rideItemIds, setRideItemIds] = useRecoilState<string[]>(rideItemIdsAtom);
  const setSummaryItemIndexes = useSetRecoilState<number[]>(summaryItemIndexesAtom);
  const setHiddenSummaryItemIndexes = useSetRecoilState<number[]>(hiddenSummaryItemIndexesAtom);
  const [rideItemTimestamps, setRideItemTimestamps] =
    useRecoilState<string[]>(rideItemTimestampsAtom);
  const [checkedTableItems, setCheckedTableItems] =
    useRecoilState<ICheckedTableItems>(checkedTableItemsAtom);
  const [allCheckedRowsOpened, setAllCheckedRowsOpened] = React.useState<boolean | null>(null);
  const [allCheckedRowsClosed, setAllCheckedRowsClosed] = React.useState<boolean | null>(null);
  const selectedVehicles = useRecoilValue<number[]>(selectedVehiclesAtom);
  // const [isSectionOpen, setIsSectionOpen] = React.useState<{ [sectionId: string]: boolean }>({});
  const [exports] = React.useState<any>(null);

  const [isModalFormOpened, setIsModalFormOpened] = useRecoilState<string>(isModalFormOpenedAtom);
  const [tripData, setTripData] = useRecoilState<ITripsOverViewRecord | null>(tripDataAtom);
  const [showLoader, setShowLoader] = React.useState(false);
  const setIsSummarySettingsMenuOpened = useSetRecoilState(isSummarySettingsMenuOpenedAtom);
  const { selectAllSectionCheckBox } = useCheckbox();
  const filterForRequest = useRecoilValue<IFilterTripsOverview>(filterForRequestAtom);
  const [sortFilter, setSortFilter] = useRecoilState<{ [key: string]: string }>(sortFilterAtom);
  const tableFiltersKey = useRecoilValue(rideBookFiltersAtom);
  const [orderForColumns, setOrderForColumns] = useRecoilState(columnsKeyOrderAtom);
  const tableSettings = localStorage.getItem('tableSettings');
  const [tableSettingsState, setTableSettingsState] = useRecoilState<ITableSettingsData | null>(
    tableSettingsStateAtom
  );
  const setLoadingNewData = useSetRecoilState(loadingDataAtom);
  const setHasMore = useSetRecoilState<boolean>(hasMoreAtom);
  const [offset, setOffset] = useRecoilState<number>(offsetAtom);
  const [prevSelectedVehicles, setPrevSelectedVehicles] = React.useState<number[]>([]);
  const [minDatepickerTimestamp, setMinDatepickerTimestamp] = React.useState<Date | null>(null);
  const [maxDatepickerTimestamp, setMaxDatepickerTimestamp] = React.useState<Date | null>(null);
  const [showSummaries, setShowSummaries] = useRecoilState(showSummariesAtom);
  const customer = useRecoilValue(customersAtom);
  const [, setActualSizeOfRecords] = useRecoilState(actualSizeOfRecordsAtom);
  const [columns, setColumns] = useRecoilState<ITripsOverviewColumn[]>(columnsAtom);
  const [loadingTableSettings, setLoadingTableSettings] = React.useState(false);
  useDocumentTitle(f({ id: 'menu.rideBook' }));
  const isActiveAdvanceFilter = useRecoilValue(isActiveAdvanceFilterAtom);
  const advancedFilterValues = useRecoilValue(advancedFiltersAtom);
  const [, paramsFromRideWidget] = useRoute('/overview-tours/:vehicleId');
  const [tableKey, setTableKey] = React.useState(0);

  const resetCheckedTableItems = React.useCallback(() => {
    setCheckedTableItems({
      mainCheckbox: false,
      intervals: [],
      intervalsBlacklist: [],
      rideIdBlacklist: {},
      rideIds: {},
      customerId: null,
      counts: {},
    });
  }, [setCheckedTableItems]);

  React.useEffect(() => {
    if (
      JSON.stringify(selectedVehicles) !== JSON.stringify(prevSelectedVehicles) ||
      selectedVehicles.length === 0
    ) {
      setPrevSelectedVehicles(selectedVehicles);
      setOffset(0);
      setTableData(null);
      setRideItemTimestamps([]);
      setSummaryItemIndexes([]);
      setHiddenSummaryItemIndexes([]);
      setRideItemIds([]);
      resetCheckedTableItems();
    }
  }, [
    prevSelectedVehicles,
    resetCheckedTableItems,
    selectedVehicles,
    setHiddenSummaryItemIndexes,
    setOffset,
    setRideItemIds,
    setRideItemTimestamps,
    setSummaryItemIndexes,
    setTableData,
  ]);

  const closeModalForm = () => {
    setIsModalFormOpened('');
    setTripData(null);
    if (paramsFromRideWidget && paramsFromRideWidget.vehicleId) {
      navigate(pathname);
    } else {
      navigate(URL_TRIPS_OVERVIEW);
    }
  };

  const handleSetOrderColumns = (newOrderColumns: { name: string; hidden: boolean }[]) => {
    const tempOrder = newOrderColumns.map((item) => item.name);

    const tempTableSettingOrder: ITripsOverviewColumn[] = [];

    columns.map((item) => {
      const findHidden = newOrderColumns.find((findItem) => item.key === findItem.name);

      if (findHidden) {
        tempTableSettingOrder.push({
          key: item.key,
          default: item.default,
          hidden: findHidden.hidden,
        });
      } else if (customer.length === 1 && item.key === CUSTOMER_KEY) {
        tempTableSettingOrder.push({ key: item.key, default: item.default, hidden: true });
      } else {
        tempTableSettingOrder.push({ key: item.key, default: item.default, hidden: item.hidden });
      }
    });

    const orderColumns = arrayOrder([...tempTableSettingOrder], [...tempOrder], 'key');

    setColumns(orderColumns);
  };

  const handleUserSettings = async () => {
    setLoadingTableSettings(true);
    const settings = await getUserSettings();
    if (settings) {
      localStorage.setItem('tableSettings', JSON.stringify(settings));
      setTableSettingsState(settings);
      setSortFilter({ timestamp: settings.sort.direction });
      handleSetOrderColumns(settings.columns);
    }
    setLoadingTableSettings(false);
  };

  React.useEffect(() => {
    if (tableData) {
      const sectionFilter = tableData.summaries.find(
        (findItem) => findItem.type !== OVERVIEW_KEY
      )?.type;

      let compareDate: string[] = [];

      const defaultLanguage = getDefaultLanguage();

      switch (sectionFilter) {
        case DAILY_KEY:
          compareDate = rideItemTimestamps.map(
            (dateItem) =>
              `${f({
                id: `date.${dayjs(dateItem).format('dddd').toLowerCase()}`,
              })} ${dayjs(dateItem).format('D.M.YYYY')}`
          );
          break;
        case WEEKLY_KEY:
          compareDate = rideItemTimestamps.map(
            (dateItem) => `${dayjs(dateItem).locale(getDayjsLocale(defaultLanguage)).week()}`
          );
          break;
        case MONTHLY_KEY:
          compareDate = rideItemTimestamps.map(
            (dateItem) => `${dayjs(dateItem).locale(getDayjsLocale(defaultLanguage)).month()}`
          );
          break;
        case TOTAL_KEY:
          compareDate = rideItemTimestamps.map(() => `all`);
          break;
        default:
      }

      const showIndex: number[] = [];
      compareDate.filter((items, i, ar) => {
        if (ar.indexOf(items) === i) {
          showIndex.push(i);
        }
      });

      setSummaryItemIndexes(showIndex);
    }
  }, [f, rideItemTimestamps, setSummaryItemIndexes, tableData]);

  const getSummaries = (
    prevData: ITripsOverviewTableData | null,
    newData: ITripsOverviewTableData
  ) => {
    if (prevData && prevData.summaries) {
      const summaries = [...prevData.summaries, ...newData.summaries];
      const uniqueSummaries: ITripsOverViewSummary[] = [];
      const timestamps: string[] = [];
      summaries.forEach((i) => {
        if (!timestamps.includes(i.from)) {
          uniqueSummaries.push(i);
          if (i.type !== OVERVIEW_KEY) {
            timestamps.push(i.from);
          }
        }
      });
      return uniqueSummaries;
    }
    return [...newData.summaries];
  };

  const handleListData = React.useCallback(
    async (signal?: AbortSignal, initOffset?: boolean) => {
      if ((showLoader || loadingTableSettings) && !initOffset) return;
      if (selectedVehicles.length > 0 && minDatepickerTimestamp && maxDatepickerTimestamp) {
        if (offset === 0) setShowLoader(true);
        setLoadingNewData(true);

        const advancedFilters: ITripsOverviewAdvancedFilterForRequest = handleFilterKey(
          {
            ...advancedFilterValues,
            timeFrom: convertTimeToSeconds(advancedFilterValues.timeFrom),
            timeTo: convertTimeToSeconds(advancedFilterValues.timeTo),
          },
          isActiveAdvanceFilter
        );

        const endpointData: IEndpoitData = {
          offset:
            JSON.stringify(selectedVehicles) !== JSON.stringify(prevSelectedVehicles) || initOffset
              ? 0
              : offset,
          filter:
            selectedVehicles.length > 0
              ? {
                  vehicle:
                    paramsFromRideWidget && paramsFromRideWidget.vehicleId
                      ? [Number(paramsFromRideWidget.vehicleId)]
                      : selectedVehicles,
                  timestamp: {
                    min: minDatepickerTimestamp,
                    max: maxDatepickerTimestamp,
                  },
                  ...advancedFilters,
                  ...filterForRequest,
                }
              : { ...advancedFilters, ...filterForRequest },
          sort: { ...sortFilter },
        };
        const listDataResponse: ITripsOverviewData = await getRidebookData(endpointData, signal);

        if (listDataResponse) {
          setRideInfoRecordsState(listDataResponse.records);
          updateRideItemsState(listDataResponse.records);
          setActualSizeOfRecords(listDataResponse.records.length - 1);
          setTableData((prevData) => ({
            availableCount: listDataResponse.availableCount,
            count: listDataResponse.count,
            summaries: getSummaries(prevData, listDataResponse),
          }));

          if (offset === 0) setShowLoader(false);
          setLoadingNewData(false);
        } else {
          if (offset === 0) setShowLoader(false);
          setLoadingNewData(false);
        }
      } else {
        setHasMore(true);
        setOffset(0);
        setTableData(null);
        setRideItemTimestamps([]);
        setSummaryItemIndexes([]);
        setHiddenSummaryItemIndexes([]);
        setRideItemIds([]);
        setLoadingNewData(false);
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      selectedVehicles,
      minDatepickerTimestamp,
      maxDatepickerTimestamp,
      setLoadingNewData,
      offset,
      isActiveAdvanceFilter.days,
      isActiveAdvanceFilter.time,
      advancedFilterValues,
      sortFilter,
      setHasMore,
      setOffset,
      loadingTableSettings,
      filterForRequest,
    ]
  );

  const getMinDatepickerValue = () => {
    const date = new Date(new Date().setHours(0, 0, 0, 0));
    date.setMonth(date.getMonth() - 24);
    return date;
  };

  const handleUpdateTable = () => {
    setOffset(0);
    setTableData(null);
    setRideItemTimestamps([]);
    setSummaryItemIndexes([]);
    setHiddenSummaryItemIndexes([]);
    setRideItemIds([]);
    handleListData(undefined, true);
    resetCheckedTableItems();
  };

  const openRideMap = () => {
    const advancedFilters: ITripsOverviewAdvancedFilterForRequest = handleFilterKey(
      {
        ...advancedFilterValues,
        timeFrom: convertTimeToSeconds(advancedFilterValues.timeFrom),
        timeTo: convertTimeToSeconds(advancedFilterValues.timeTo),
      },
      isActiveAdvanceFilter
    );
    const filter = {
      vehicle:
        paramsFromRideWidget && paramsFromRideWidget.vehicleId
          ? [Number(paramsFromRideWidget.vehicleId)]
          : selectedVehicles,
      ...advancedFilters,
      ...filterForRequest,
    };
    const ridesBulkData = { ...checkedTableItems, filter };
    localForageStore.setItem('multi-rides', ridesBulkData);
    window.open(`${URL_TRIP_MAP}`, '_blank');
  };

  const getDatepickerValue = (index: number) => {
    const value =
      !datepickerSettings ||
      !datepickerSettings.timestamp ||
      !datepickerSettings.timestamp.date[index]
        ? ''
        : datepickerSettings.timestamp.date[index];
    if (value) {
      const defaultLanguage = getDefaultLanguage();
      return dayjs(value).locale(getDayjsLocale(defaultLanguage)).format('D.M.YYYY HH:mm');
    }
    return '';
  };

  const handleSaveTableSettings = async (data: ITableSettingsData): Promise<boolean> => {
    if (isDemo()) {
      return true;
    }
    if (isNotDemo()) {
      const saveSettings = await saveTableSettingAndSummary(data);

      if (saveSettings) {
        setTableSettingsState(saveSettings.value);
        localStorage.setItem('tableSettings', JSON.stringify(saveSettings.value));
      }
      if (saveSettings) {
        return true;
      }
      return false;
    }
    return true;
  };

  const handleDateFromTo = () => {
    let tempDateFrom =
      sortFilter.timestamp === DESCENDING_KEY
        ? rideItemTimestamps[rideItemTimestamps.length - 1]
        : rideItemTimestamps[0];
    let tempDateTo =
      sortFilter.timestamp === DESCENDING_KEY
        ? rideItemTimestamps[0]
        : rideItemTimestamps[rideItemTimestamps.length - 1];

    // Convert to ISO string if they are Date objects or if they are string representations of dates
    if (tempDateFrom && typeof tempDateFrom !== 'string') {
      tempDateFrom = new Date(tempDateFrom).toISOString();
    }
    if (tempDateTo && typeof tempDateTo !== 'string') {
      tempDateTo = new Date(tempDateTo).toISOString();
    }

    // If rideItemIds is empty and datepickerSettings exist, use settings dates
    if (rideItemIds.length === 0 && datepickerSettings) {
      tempDateFrom = new Date(datepickerSettings.timestamp.date[0]).toISOString();
      tempDateTo = new Date(datepickerSettings.timestamp.date[1]).toISOString();
    }

    return { tempDateFrom, tempDateTo };
  };

  const handleSummary = async (data: ITableSummaryData) => {
    setSummaryItemIndexes([]);
    setHiddenSummaryItemIndexes([]);
    const { tempDateFrom, tempDateTo } = handleDateFromTo();

    let tableColumns: { name: string; hidden: boolean }[] = orderForColumns
      .filter((item) => item.key !== ACTIONS_KEY)
      .map((i) => {
        return { name: i.key, hidden: i.hidden };
      });

    let tempData = data;

    if (data.periodicSummaries.displayData && data.periodicSummaries.displayData.length === 0) {
      tempData = {
        ...data,
        periodicSummaries: {
          ...data.periodicSummaries,
          displayData: null,
        },
      };
    }

    if (tableColumns.length === 0 || !tableColumns[0].name) {
      if (tableSettings) {
        const tempTableSetting: { name: string; hidden: boolean }[] =
          JSON.parse(tableSettings).columns;
        tableColumns = tempTableSetting;
      }
    }

    const allData = {
      columns: tableColumns,
      ...tempData,
      showSummaries,
      sort: { column: TIMESTAMP_KEY, direction: sortFilter.timestamp },
    };
    setIsSummarySettingsMenuOpened(false);

    const saveSettings = await handleSaveTableSettings(allData);

    if (saveSettings) {
      const advancedFilters: ITripsOverviewAdvancedFilterForRequest = handleFilterKey(
        {
          ...advancedFilterValues,
          timeFrom: convertTimeToSeconds(advancedFilterValues.timeFrom),
          timeTo: convertTimeToSeconds(advancedFilterValues.timeTo),
        },
        isActiveAdvanceFilter
      );

      const dataForSummaries: ITableSummariesFilter = {
        filter:
          selectedVehicles.length > 0
            ? {
                vehicle:
                  paramsFromRideWidget && paramsFromRideWidget.vehicleId
                    ? [Number(paramsFromRideWidget.vehicleId)]
                    : selectedVehicles,
                timestamp: {
                  min: datepickerSettings?.timestamp.date[0],
                  max: datepickerSettings?.timestamp.date[1],
                },
                ...advancedFilters,
                ...filterForRequest,
              }
            : { ...advancedFilters, ...filterForRequest },
        sort: { ...sortFilter },
        dateFrom: tempDateFrom,
        dateTo: tempDateTo,
      };

      if (selectedVehicles.length > 0) {
        setShowLoader(true);

        const response = await getTableSummaries(dataForSummaries);

        if (response) {
          setTableData((prevData) =>
            prevData ? { ...prevData, summaries: response.summaries } : null
          );
        }
        setShowLoader(false);
      }
    }

    resetCheckedTableItems();
  };

  const updateSummaryData = async () => {
    const { tempDateFrom, tempDateTo } = handleDateFromTo();
    const advancedFilters: ITripsOverviewAdvancedFilterForRequest = handleFilterKey(
      {
        ...advancedFilterValues,
        timeFrom: convertTimeToSeconds(advancedFilterValues.timeFrom),
        timeTo: convertTimeToSeconds(advancedFilterValues.timeTo),
      },
      isActiveAdvanceFilter
    );

    const dataForSummaries: ITableSummariesFilter = {
      filter:
        selectedVehicles.length > 0
          ? {
              vehicle:
                paramsFromRideWidget && paramsFromRideWidget.vehicleId
                  ? [Number(paramsFromRideWidget.vehicleId)]
                  : selectedVehicles,
              timestamp: {
                min: datepickerSettings?.timestamp.date[0],
                max: datepickerSettings?.timestamp.date[1],
              },
              ...advancedFilters,
              ...filterForRequest,
            }
          : { ...advancedFilters, ...filterForRequest },
      sort: { ...sortFilter },
      dateFrom: tempDateFrom,
      dateTo: tempDateTo,
    };

    setShowLoader(true);

    const response = await getTableSummaries(dataForSummaries);

    if (response) {
      setTableData((prevData) =>
        prevData ? { ...prevData, summaries: response.summaries } : null
      );
    }
    setShowLoader(false);
  };

  const switchSummarySettings = async (value: boolean) => {
    if (tableSettings) {
      const tempTableSetting: ITableSettingsData = JSON.parse(tableSettings);

      const dataForRequest = {
        ...tempTableSetting,
        showSummaries: value,
      };

      if (isNotDemo()) {
        const saveSettings = await saveTableSettingAndSummary(dataForRequest);

        if (saveSettings) {
          const settings = saveSettings.value;
          setTableSettingsState(settings);
          localStorage.setItem('tableSettings', JSON.stringify(settings));
        }
      }
      if (selectedVehicles.length > 0) {
        updateSummaryData();
      }
    }
  };

  const handleChangeRideType = async (action: string) => {
    setShowLoader(true);
    const advancedFilters: ITripsOverviewAdvancedFilterForRequest = handleFilterKey(
      {
        ...advancedFilterValues,
        timeFrom: convertTimeToSeconds(advancedFilterValues.timeFrom),
        timeTo: convertTimeToSeconds(advancedFilterValues.timeTo),
      },
      isActiveAdvanceFilter
    );
    const filter = {
      vehicle:
        paramsFromRideWidget && paramsFromRideWidget.vehicleId
          ? [Number(paramsFromRideWidget.vehicleId)]
          : selectedVehicles,
      ...advancedFilters,
      ...filterForRequest,
    };
    const res = await changeRidePrivacy(action, null, checkedTableItems, filter);
    if (res) {
      handleUpdateTable();
    }
    setShowLoader(false);
  };

  const handleAction = (action: string) => {
    let type = '';

    switch (action) {
      case NEW_RIDE_ACTION:
        type = 'new-ride';
        break;
      case RIDE_PURPOSE_ACTION:
        type = 'usage';
        break;
      case PARKING_PURPOSE_ACTION:
        type = 'usage-parking';
        break;
      case CHANGE_DRIVER_ACTION:
        type = 'driver';
        break;
      case RIDE_COMMENT_ACTION:
        type = 'comment';
        break;
      case PARKING_COMMENT_ACTION:
        type = 'comment-parking';
        break;
      case TYPE_PRIVATE_ACTION:
        handleChangeRideType(action);
        break;
      case TYPE_PUBLIC_ACTION:
        handleChangeRideType(action);
        break;
      case SHOW_RIDE_ACTION:
        openRideMap();
        break;
      case 'action_show':
        setAllCheckedRowsOpened(!allCheckedRowsOpened);
        break;
      case 'action_hide':
        setAllCheckedRowsClosed(!allCheckedRowsClosed);
        break;
      default:
    }
    setIsModalFormOpened(type);
  };

  React.useEffect(() => {
    if (tableSettings) {
      const tempTableSetting: ITableSettingsData = JSON.parse(tableSettings);

      const tempTableSettingOrder: { key: string; hidden: boolean }[] = [];

      tempTableSetting.columns.map((item) => {
        tempTableSettingOrder.push({ key: item.name, hidden: item.hidden });
      });
      tempTableSettingOrder.push({ key: 'actions', hidden: false });

      setShowSummaries(tempTableSetting.showSummaries);
      setSortFilter({ timestamp: tempTableSetting.sort.direction });

      setOrderForColumns(tempTableSettingOrder);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    setOffset(0);
    setTableData(null);
    setRideItemTimestamps([]);
    setSummaryItemIndexes([]);
    setHiddenSummaryItemIndexes([]);
    setRideItemIds([]);
    resetCheckedTableItems();
    setHasMore(true);
  }, [
    resetCheckedTableItems,
    setHasMore,
    setHiddenSummaryItemIndexes,
    setOffset,
    setRideItemIds,
    setRideItemTimestamps,
    setSummaryItemIndexes,
    setTableData,
    minDatepickerTimestamp,
    maxDatepickerTimestamp,
  ]);

  React.useEffect(() => {
    if (
      datepickerSettings &&
      datepickerSettings.timestamp &&
      datepickerSettings.timestamp.date &&
      datepickerSettings.timestamp.date.length === 2
    ) {
      if (
        dayjs(datepickerSettings.timestamp.date[0]).format('DD.MM.YYYY HH:mm') !==
        dayjs(minDatepickerTimestamp).format('DD.MM.YYYY HH:mm')
      ) {
        setMinDatepickerTimestamp(datepickerSettings.timestamp.date[0]);
      }
      if (
        dayjs(datepickerSettings.timestamp.date[1]).format('DD.MM.YYYY HH:mm') !==
        dayjs(maxDatepickerTimestamp).format('DD.MM.YYYY HH:mm')
      ) {
        setMaxDatepickerTimestamp(datepickerSettings.timestamp.date[1]);
      }
    }
  }, [maxDatepickerTimestamp, minDatepickerTimestamp, datepickerSettings]);

  React.useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    handleListData(signal);
    return () => {
      if (rideItemIds.length > 0) {
        controller.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleListData]);

  React.useEffect(() => {
    if (pathname === `${URL_TRIPS_OVERVIEW}/add`) {
      setIsModalFormOpened('new-ride');
    } else if (params && params.vehicleId && params.rideId) {
      setIsModalFormOpened(`new-ride-${params.vehicleId}-${params.rideId}`);
    }
    handleUserSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getRideId = (ride: string) => {
    const rideArr = ride.split('-');
    if (rideArr.length === 4) {
      return rideArr[2];
    }
    return null;
  };

  const getVehicleId = (ride: string) => {
    const rideArr = ride.split('-');
    if (rideArr.length === 4) {
      return rideArr[3];
    }
    return null;
  };

  const handleConfirmRemoveRide = async () => {
    const vehicleId = getVehicleId(isModalFormOpened);
    const rideId = getRideId(isModalFormOpened);
    if (rideId && vehicleId) {
      const result = await removeRide(rideId, vehicleId);
      if (result) {
        handleUpdateTable();
      }
    }
    closeModalForm();
  };

  const handleDayTimeModal = (event?: React.MouseEvent<HTMLElement, MouseEvent>) => {
    if (event?.currentTarget.id) {
      setIsModalFormOpened(IS_DAY_TIME);
    }
  };

  React.useEffect(() => {
    const visibleColumnsCount = columns.filter((col) => !col.hidden).length;
    if (tableKey !== visibleColumnsCount) {
      setTableKey(visibleColumnsCount);
    }
  }, [columns, tableKey]);

  const shouldRenderAdvancedFilterChips =
    isActiveAdvanceFilter.days ||
    isActiveAdvanceFilter.time ||
    isActiveAdvanceFilter.comment ||
    isActiveAdvanceFilter.ridebookPurpose ||
    isActiveAdvanceFilter.announcementAlarm ||
    isActiveAdvanceFilter.announcementWaypoint ||
    isActiveAdvanceFilter.announcementEvent ||
    isActiveAdvanceFilter.announcementBluecoin;

  return (
    <div id="page-content-wrapper-div" style={{ height: '100%', overflowY: 'auto' }} key={tableKey}>
      <div className="w-page">
        <div className="w-page-content">
          {paramsFromRideWidget && <ExportDownloadToast />}
          <div className="page-header">
            <h4 className="page-header__title">{f({ id: 'menu.rideBook' })}</h4>
            <OnlineHelp topic="ride-book" />
          </div>
          <div className="container-fluid">
            <section className="basic-box border-top-thin border-top-gray">
              <div className="basic-box__content basic-box__content--padding-sm">
                <div className="row no-gutters mb-2">
                  {tableSettingsState && (
                    <SummarySettings
                      data={tableSettingsState}
                      handleSummary={handleSummary}
                      switchSummarySettings={switchSummarySettings}
                    />
                  )}
                  <TableSettings />
                  <AddButton handleAction={handleAction} />
                  <div className="col col-md" />
                  <div className="col-auto col-md-12 mt-2 ml-2 mt-md-0 ml-md-0 col-lg-auto order-5 order-lg-4 mt-lg-2 mr-2">
                    {`${f({ id: 'table.results.total' })}: `}
                    <strong data-cy="ride-book-count">
                      {(tableData && tableData.count !== null) || !tableData ? (
                        `${tableData?.count ? `${tableData.count}` : `0`}`
                      ) : (
                        <i className="cmd-icons cmd-icon-gdpr-document-lock-dark-gray" />
                      )}
                    </strong>
                  </div>
                  <div className="col-auto order-4 order-lg-5">
                    {PermissionsService.hasAccess(`${PERMISSION_REPORT}05`, ['read'], null) && (
                      <Ridebook
                        actionUrl={exports?.actionUrl}
                        fileName={exports?.fileName}
                        formats={exports?.formats}
                        dates={
                          datepickerSettings &&
                          datepickerSettings.timestamp &&
                          datepickerSettings.timestamp.date
                            ? datepickerSettings.timestamp.date
                            : null
                        }
                      />
                    )}
                  </div>
                </div>
                <div className="d-flex flex-wrap mb-2">
                  <TableDatepicker
                    name="timestamp"
                    allowedRange={[
                      getMinDatepickerValue(),
                      new Date(new Date().setHours(23, 59, 59, 999)),
                    ]}
                    isActive={true}
                    type="chip"
                    isDayTime={true}
                    onClickEvent={handleDayTimeModal}
                  >
                    <div
                      className="label label-primary mr-1 mb-1 label--filter"
                      style={{ cursor: 'pointer', position: 'relative' }}
                      data-cy="datapicker-filter-timestamp"
                    >
                      <span>
                        {`${f({ id: 'costsOverviewForm.dateTime' })}: ${getDatepickerValue(
                          0
                        )} - ${getDatepickerValue(1)}`}
                      </span>
                    </div>
                  </TableDatepicker>

                  {shouldRenderAdvancedFilterChips && <AdvancedFilterChips />}
                  {tableFiltersKey && tableFiltersKey.map((id) => <FilterChips key={id} id={id} />)}
                </div>
                {showLoader && <CmdLoader inContent={true} />}
                <Table
                  updateTable={handleUpdateTable}
                  handleAction={handleAction}
                  selectAllSectionCheckBox={selectAllSectionCheckBox}
                  allCheckedRowsOpened={allCheckedRowsOpened}
                  allCheckedRowsClosed={allCheckedRowsClosed}
                />
              </div>
              {(isModalFormOpened === 'comment' || isModalFormOpened === 'comment-parking') && (
                <AddComment
                  data={tripData}
                  type={isModalFormOpened}
                  closeCallback={closeModalForm}
                  updateTable={handleUpdateTable}
                />
              )}
              {isModalFormOpened === IS_DRIVER && (
                <ChangeDriver
                  data={tripData}
                  type={isModalFormOpened}
                  closeCallback={closeModalForm}
                  updateTable={handleUpdateTable}
                />
              )}
              {isModalFormOpened === IS_DAY_TIME && <DayTimeFilter handleClose={closeModalForm} />}
              {isModalFormOpened === INFO_FILTER && (
                <InforRideFilter handleClose={closeModalForm} />
              )}
              {(isModalFormOpened === 'usage' || isModalFormOpened === 'usage-parking') && (
                <AddUsage
                  data={tripData}
                  type={isModalFormOpened}
                  closeCallback={closeModalForm}
                  updateTable={handleUpdateTable}
                />
              )}
              {isModalFormOpened === 'detail' && tripData && (
                <TripDetail data={tripData} closeCallback={closeModalForm} />
              )}
              {isModalFormOpened.includes('new-ride') && (
                <NewRideForm
                  handleClose={closeModalForm}
                  updateTable={handleUpdateTable}
                  rideId={getRideId(isModalFormOpened)}
                  vehicleId={getVehicleId(isModalFormOpened)}
                />
              )}
              {isModalFormOpened.includes('remove-ride') && (
                <ConfirmDialog
                  title={f({ id: 'tripsOverview.modal.newRide.title.remove' })}
                  cancelCallback={closeModalForm}
                  continueCallback={handleConfirmRemoveRide}
                  cancelButtonTitle={f({ id: 'confirm.dialog.answerNo' })}
                  continueButtonTitle={f({ id: 'confirm.dialog.answerYes' })}
                >
                  {f({ id: 'tripsOverview.modal.newRide.title.confirm' })}
                </ConfirmDialog>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

export default React.memo(TripsOverview);
